import React from 'react'
import { CTATheme, mq } from '../../utils'
import { CTALink } from '../CTALink'
import { Image } from '../Image'
import { TagGroup } from '../TagGroup'
import { Text } from '../Text'
import { IProps } from './index'

export const CardWithImage: React.FC<IProps> = ({
  title,
  body,
  cta,
  imageRef,
  tags,
  centerAlign,
  eyebrow,
}: IProps) => {
  const CardContent = (
    <div className="card-with-image">
      {/* Image */}
      <div className="card-with-image__media">
        <Image {...imageRef} borderRadius={20} classNames="card-with-image__image" />
      </div>

      <div className="card-with-image__content">
        {/* Tags */}
        {!!tags && !!tags.length && (
          <div className="card-with-image__tags">
            <TagGroup tagData={tags} />
          </div>
        )}
        {eyebrow && (
          <div className="card-with-image__eyebrow">
            <Text brand="default" size="eyebrows">
              {eyebrow}
            </Text>
          </div>
        )}
        {/* Title */}
        {title && (
          <div className="card-with-image__title">
            <Text brand="default" size={body ? 'heading-5' : 'heading-4'}>
              {title}
            </Text>
          </div>
        )}
        {/* Body */}
        {body && (
          <div className="card-with-image__body">
            <Text brand="default" size="body">
              {body}
            </Text>
          </div>
        )}
        {/* CTA */}
        {cta && (
          <div className="card-with-image__cta">
            <CTALink {...cta} bgColor={CTATheme.LinkOnlyBlack} arrow />
          </div>
        )}
      </div>
      <style jsx>
        {`
          .card-with-image {
            height: 100%;
            display: flex;
            flex-direction: column;

            &__media {
              margin: ${centerAlign ? '0 auto' : '0'};
              border-radius: 1.25rem;
              overflow: hidden;
            }

            &__image {
              width: 100%;
              height: auto;
            }

            &__media + &__content {
              margin-top: 2rem;
            }

            &__content {
              flex: 1;
              display: flex;
              flex-direction: column;
              text-align: ${centerAlign ? 'center' : 'left'};
            }

            &__eyebrow {
              margin-bottom: 0.75rem;
            }

            &__title + *,
            &__tags + * {
              margin-top: 0.75rem;
            }

            &__title {
              word-break: break-word;
            }

            &__body {
              margin-bottom: auto;
            }

            * + &__cta {
              margin-top: 1.5rem;
            }

            &__cta {
              display: ${centerAlign ? 'block' : 'inline-block'};
              text-align: ${centerAlign ? 'center' : 'left'};
            }
          }

          @media (${mq.mobile}) {
            .card-width-image__title:nth-last-child(2) {
              margin-bottom: auto;
            }
          }

          @media (${mq.tablet}) {
            .card-with-image {
              &__eyebrow {
                margin-bottom: 1.75rem;
              }
              &__title + *,
              &__tags + * {
                margin-top: 1.125rem;
              }

              * + &__cta {
                margin-top: 1.75rem;
              }
            }
          }
        `}
      </style>
      <style jsx global>{`
        .card-with-image__title .rich-text p {
          line-height: 1.4;
        }
      `}</style>
    </div>
  )

  if (cta) {
    return (
      <a
        className="card-with-image__link"
        href={cta.url}
        target={cta.newWindow ? '_blank' : ''}
        rel={cta.newWindow ? 'noopener nofollow' : ''}
      >
        {CardContent}
        <style jsx>
          {`
            .card-with-image {
              &__link {
                display: flex;
                height: 100%;
                color: initial;
              }

              &__link,
              &__link:hover {
                text-decoration: none;

                &:global(.cta-link) {
                  text-decoration: underline;
                }
              }

              &__media {
                overflow: hidden;
                border-radius: 1.25rem;
              }
            }
          `}
        </style>
      </a>
    )
  }

  return CardContent
}
